import React, { useEffect, useRef, useState } from "react";
import Map from "ol/Map";
import View from "ol/View";
import Overlay from "ol/Overlay";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import { fromLonLat, get as getProjection } from "ol/proj";
import { createEmpty, extend, isEmpty } from "ol/extent";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style";
import { formatDateTime } from "./wplDashboardHelpers";

// Esri Light Gray Canvas: keyless, and the same provider family as OlMap. CARTO's
// raster tiles now return an "API KEY REQUIRED" watermark tile without a key.
const TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const TILE_MAX_ZOOM = 16;
const TILE_ATTRIBUTION =
  'Tiles © <a href="https://www.esri.com">Esri</a> — Esri, HERE, Garmin, © OpenStreetMap contributors';

// Minimum bbox padding in degrees (~1 km) so single-turbine farms stay visible.
const MIN_PADDING_DEG = 0.01;
const PADDING_RATIO = 0.15;

// Show windfarm names from roughly continent zoom (~z4) inward.
const LABEL_MAX_RESOLUTION = 12000;

// Show turbine names only when zoomed close to the farm (~z12).
const TURBINE_LABEL_MAX_RESOLUTION = 40;

const turbineColor = (turbine) => {
  if (turbine.outside_iec_count > 0) return "#f5894a";
  if (turbine.strike_count > 0) return "#38b97c";
  return "#8fa8a0";
};

const buildTurbineFeature = (turbine) => {
  const feature = new Feature({
    geometry: new Point(fromLonLat([turbine.lon, turbine.lat], "EPSG:3857")),
    turbine_info: turbine,
  });
  const image = new CircleStyle({
    radius: 5,
    fill: new Fill({ color: turbineColor(turbine) }),
    stroke: new Stroke({ color: "#ffffff", width: 1.5 }),
  });
  const label = new Text({
    text: turbine.turbine_name || "",
    font: "600 11px sans-serif",
    offsetY: -14,
    fill: new Fill({ color: "#163a34" }),
    stroke: new Stroke({ color: "rgba(255, 255, 255, 0.9)", width: 3 }),
  });
  feature.setStyle((f, resolution) =>
    new Style({
      image,
      text: resolution < TURBINE_LABEL_MAX_RESOLUTION ? label : undefined,
    })
  );
  return feature;
};

const farmColors = (farm) =>
  farm.outside_iec_count > 0
    ? {
        stroke: "#f5894a",
        fill: "rgba(245, 137, 74, 0.25)",
        highlightFill: "rgba(245, 137, 74, 0.5)",
        marker: "#f5894a",
      }
    : {
        stroke: "#38b97c",
        fill: "rgba(56, 185, 124, 0.3)",
        highlightFill: "rgba(56, 185, 124, 0.55)",
        marker: "#38b97c",
      };

const paddedBboxRing = (bbox) => {
  const latPad = Math.max((bbox.max_lat - bbox.min_lat) * PADDING_RATIO, MIN_PADDING_DEG);
  const lonPad = Math.max((bbox.max_lon - bbox.min_lon) * PADDING_RATIO, MIN_PADDING_DEG);
  const minLat = bbox.min_lat - latPad;
  const maxLat = bbox.max_lat + latPad;
  const minLon = bbox.min_lon - lonPad;
  const maxLon = bbox.max_lon + lonPad;
  return [
    [minLon, minLat],
    [maxLon, minLat],
    [maxLon, maxLat],
    [minLon, maxLat],
    [minLon, minLat],
  ].map((lonLat) => fromLonLat(lonLat, "EPSG:3857"));
};

const buildFarmFeatures = (farm, isHighlighted = false) => {
  const colors = farmColors(farm);

  const bboxFeature = new Feature({
    geometry: new Polygon([paddedBboxRing(farm.bbox)]),
    windfarm_id: farm.windfarm_id,
  });
  bboxFeature.setStyle(
    new Style({
      stroke: new Stroke({
        color: colors.stroke,
        width: isHighlighted ? 3 : 1.5,
      }),
      fill: new Fill({
        color: isHighlighted ? colors.highlightFill : colors.fill,
      }),
    })
  );

  const markerFeature = new Feature({
    geometry: new Point(fromLonLat([farm.center.lon, farm.center.lat], "EPSG:3857")),
    windfarm_id: farm.windfarm_id,
  });
  const markerImage = new CircleStyle({
    radius: isHighlighted ? 9 : 6,
    fill: new Fill({ color: colors.marker }),
    stroke: new Stroke({ color: "#ffffff", width: isHighlighted ? 3 : 2 }),
  });
  const markerLabel = new Text({
    text: farm.windfarm_name || "",
    font: isHighlighted ? "700 13px sans-serif" : "600 12px sans-serif",
    offsetY: isHighlighted ? -19 : -16,
    fill: new Fill({ color: "#163a34" }),
    stroke: new Stroke({ color: "rgba(255, 255, 255, 0.9)", width: 3 }),
  });
  // Labels only appear from continent zoom inward to avoid world-view clutter;
  // a highlighted farm (hovered in the chart/table) always shows its name.
  markerFeature.setStyle((feature, resolution) =>
    new Style({
      image: markerImage,
      text:
        isHighlighted || resolution < LABEL_MAX_RESOLUTION
          ? markerLabel
          : undefined,
    })
  );

  return [bboxFeature, markerFeature];
};

// When a farm's turbines are drawn, its solid fill would drown them out: keep
// only a thin outline (the turbines carry the labels and colors).
const buildSelectedFarmOutline = (farm) => {
  const colors = farmColors(farm);
  const feature = new Feature({
    geometry: new Polygon([paddedBboxRing(farm.bbox)]),
    windfarm_id: farm.windfarm_id,
  });
  feature.setStyle(
    new Style({
      stroke: new Stroke({ color: colors.stroke, width: 1.5, lineDash: [6, 4] }),
    })
  );
  return feature;
};

export default function WplWindfarmsMap({
  windfarms,
  focusRequest,
  onFarmClick,
  selectedFarmId,
  selectedFarmTurbines,
  highlightedFarmId,
}) {
  const mapElementRef = useRef(null);
  const tooltipElementRef = useRef(null);
  const tooltipOverlayRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const turbineSourceRef = useRef(null);
  const windfarmsRef = useRef(windfarms);
  const onFarmClickRef = useRef(onFarmClick);
  const [map, setMap] = useState(null);
  const [hoveredFarm, setHoveredFarm] = useState(null);
  const [hoveredTurbine, setHoveredTurbine] = useState(null);

  windfarmsRef.current = windfarms;
  onFarmClickRef.current = onFarmClick;

  // Create the map once.
  useEffect(() => {
    const vectorSource = new VectorSource();
    vectorSourceRef.current = vectorSource;
    const turbineSource = new VectorSource();
    turbineSourceRef.current = turbineSource;

    const olMap = new Map({
      target: mapElementRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: TILE_URL,
            maxZoom: TILE_MAX_ZOOM,
            attributions: TILE_ATTRIBUTION,
          }),
        }),
        new VectorLayer({ source: vectorSource }),
        new VectorLayer({ source: turbineSource, zIndex: 2 }),
      ],
      view: new View({
        center: fromLonLat([10, 50], "EPSG:3857"),
        zoom: 4,
        minZoom: 2,
        // Lock panning to a single world (no globe-style wrap-around).
        extent: getProjection("EPSG:3857").getExtent(),
        constrainOnlyCenter: false,
      }),
    });

    const tooltipOverlay = new Overlay({
      element: tooltipElementRef.current,
      offset: [12, 0],
      positioning: "center-left",
      stopEvent: false,
    });
    olMap.addOverlay(tooltipOverlay);
    tooltipOverlayRef.current = tooltipOverlay;

    const featuresAtPixel = (pixel) => {
      let turbine = null;
      let windfarmId = null;
      olMap.forEachFeatureAtPixel(pixel, (feature) => {
        if (!turbine && feature.get("turbine_info")) {
          turbine = feature.get("turbine_info");
        }
        if (windfarmId === null) {
          windfarmId = feature.get("windfarm_id") ?? null;
        }
      });
      const farm =
        windfarmId === null
          ? null
          : (windfarmsRef.current || []).find(
              (f) => f.windfarm_id === windfarmId
            ) || null;
      return { farm, turbine };
    };

    const farmAtPixel = (pixel) => featuresAtPixel(pixel).farm;

    olMap.on("pointermove", (event) => {
      if (event.dragging) return;
      const { farm, turbine } = featuresAtPixel(event.pixel);
      setHoveredFarm(farm);
      setHoveredTurbine(turbine);
      olMap.getTargetElement().style.cursor = farm || turbine ? "pointer" : "";
      // Flip the tooltip to the pointer's other side near the right edge so
      // it is not clipped by the card (overflow: hidden).
      const mapWidth = (olMap.getSize() || [0])[0];
      if (event.pixel[0] > mapWidth * 0.6) {
        tooltipOverlay.setPositioning("center-right");
        tooltipOverlay.setOffset([-12, 0]);
      } else {
        tooltipOverlay.setPositioning("center-left");
        tooltipOverlay.setOffset([12, 0]);
      }
      tooltipOverlay.setPosition(farm || turbine ? event.coordinate : undefined);
    });

    olMap.on("click", (event) => {
      const farm = farmAtPixel(event.pixel);
      if (!farm) return;
      olMap.getView().fit(new Polygon([paddedBboxRing(farm.bbox)]).getExtent(), {
        duration: 400,
        padding: [40, 40, 40, 40],
        maxZoom: 14,
      });
      if (onFarmClickRef.current) onFarmClickRef.current(farm);
    });

    setMap(olMap);
    return () => {
      olMap.setTarget(null);
    };
  }, []);

  const turbinesShownForFarmId =
    (selectedFarmTurbines || []).length > 0 ? selectedFarmId : null;

  // (Re)build features when the data changes, then fit the combined extent.
  useEffect(() => {
    const vectorSource = vectorSourceRef.current;
    if (!map || !vectorSource) return;

    const hadFeatures = vectorSource.getFeatures().length > 0;
    vectorSource.clear();
    (windfarms || []).forEach((farm) => {
      if (!farm.bbox || !farm.center) return;
      if (farm.windfarm_id === turbinesShownForFarmId) {
        vectorSource.addFeature(buildSelectedFarmOutline(farm));
      } else {
        vectorSource.addFeatures(
          buildFarmFeatures(farm, farm.windfarm_id === highlightedFarmId)
        );
      }
    });

    // Only refit on the initial data load — not when a selection restyles the
    // features (that would fight the click-to-zoom animation).
    if (hadFeatures) return;
    const combined = createEmpty();
    vectorSource.getFeatures().forEach((feature) => {
      extend(combined, feature.getGeometry().getExtent());
    });
    if (!isEmpty(combined)) {
      map.getView().fit(combined, { padding: [50, 50, 50, 50], maxZoom: 9 });
    }
  }, [map, windfarms, turbinesShownForFarmId, highlightedFarmId]);

  // Show the selected farm's individual turbines once its detail is loaded.
  useEffect(() => {
    const turbineSource = turbineSourceRef.current;
    if (!map || !turbineSource) return;
    turbineSource.clear();
    (selectedFarmTurbines || []).forEach((turbine) => {
      if (turbine.lat == null || turbine.lon == null) return;
      turbineSource.addFeature(buildTurbineFeature(turbine));
    });
  }, [map, selectedFarmTurbines]);

  // Zoom to a farm when the chart asks for it; a null farm means "zoom back
  // out to the full overview" (e.g. after closing the highlights panel).
  useEffect(() => {
    if (!map || !focusRequest) return;
    if (focusRequest.farm) {
      map.getView().fit(
        new Polygon([paddedBboxRing(focusRequest.farm.bbox)]).getExtent(),
        { duration: 400, padding: [40, 40, 40, 40], maxZoom: 14 }
      );
      return;
    }
    const combined = createEmpty();
    (windfarmsRef.current || []).forEach((farm) => {
      if (!farm.bbox) return;
      extend(
        combined,
        new Polygon([paddedBboxRing(farm.bbox)]).getExtent()
      );
    });
    if (!isEmpty(combined)) {
      map.getView().fit(combined, {
        duration: 400,
        padding: [50, 50, 50, 50],
        maxZoom: 9,
      });
    }
  }, [map, focusRequest]);

  return (
    <div className="bd-windfarms-map">
      <div ref={mapElementRef} className="bd-windfarms-map-canvas" />
      <div
        ref={tooltipElementRef}
        className="bd-windfarms-map-tooltip"
        style={{ display: hoveredFarm || hoveredTurbine ? "block" : "none" }}
      >
        {hoveredTurbine && (
          <>
            <p className="bd-windfarms-tooltip-title">
              {hoveredTurbine.turbine_name}
            </p>
            <p className="bd-windfarms-tooltip-subtitle">
              Turbine · last 12 months
            </p>
            <dl className="bd-windfarms-tooltip-facts">
              <div>
                <dt>Strikes</dt>
                <dd>{hoveredTurbine.strike_count}</dd>
              </div>
              <div>
                <dt>Outside IEC</dt>
                <dd>{hoveredTurbine.outside_iec_count}</dd>
              </div>
              <div>
                <dt>Last strike</dt>
                <dd>
                  {hoveredTurbine.last_strike_at
                    ? formatDateTime(hoveredTurbine.last_strike_at)
                    : "—"}
                </dd>
              </div>
            </dl>
          </>
        )}
        {!hoveredTurbine && hoveredFarm && (
          <>
            <p className="bd-windfarms-tooltip-title">
              {hoveredFarm.windfarm_name}
            </p>
            <p className="bd-windfarms-tooltip-subtitle">
              {hoveredFarm.owner || hoveredFarm.customer || "Unknown customer"}
            </p>
            <dl className="bd-windfarms-tooltip-facts">
              <div>
                <dt>Turbines</dt>
                <dd>{hoveredFarm.turbine_count}</dd>
              </div>
              <div>
                <dt>Strikes</dt>
                <dd>{hoveredFarm.strike_count}</dd>
              </div>
              <div>
                <dt>Outside IEC</dt>
                <dd>{hoveredFarm.outside_iec_count}</dd>
              </div>
              <div>
                <dt>Last notification</dt>
                <dd>
                  {hoveredFarm.last_notification_at
                    ? formatDateTime(hoveredFarm.last_notification_at)
                    : "No notifications sent"}
                </dd>
              </div>
            </dl>
          </>
        )}
      </div>
    </div>
  );
}
